import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { IntakeMedicalSelectionType } from '../../../../../../../store/questionnaires/intake';

export default function useIntakeMedicalSelections() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();

  const earlyInterventions = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.medical_info?.early_interventions ?? [],
  );
  const pregnancyIssues = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.medical_info?.pregnancy_issues ?? [],
  );
  const regressions = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.medical_info?.regressions ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createMedicalSelectionLoading ||
      state.intakeQuestionnaire.deleteMedicalSelectionLoading,
  );

  const createSelection = useCallback(
    async (selectionType: IntakeMedicalSelectionType, value: string) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeMedicalSelection({
          intakeQuestionnaireId,
          data: { selection_type: selectionType, value },
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeMedicalSelection.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const deleteSelection = useCallback(
    async (selectionType: IntakeMedicalSelectionType, selectionId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeMedicalSelection({
          intakeQuestionnaireId,
          selectionType,
          selectionId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeMedicalSelection.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return {
    earlyInterventions,
    pregnancyIssues,
    regressions,
    isSaving,
    createSelection,
    deleteSelection,
  };
}
